<template>
    <div>
        <edit-member-availability-dialog
            v-model="dialog_availability"
            :modelProp="selected_availability"
            :memberProp="member"
            :constants="constant"
            :valuelists="valuelist"
            :productions="production_names"
            @successful-save="$emit('successful-save')"
            @successful-delete="$emit('successful-delete')"
        ></edit-member-availability-dialog>

        <div class="tw-flex tw-flex-row tw-gap-4 tw-p-1">
            <div class="tw-flex tw-flex-col tw-w-1/3 tw-gap-4"><!-- left side -->
                <internal-table-section button-text="Add Availability" @button-clicked="addRecord" class="tw-w-full" :isLoading="is_loading">
                </internal-table-section>

                <internal-table-section section-title="Memo" button-text="Save Memo" @button-clicked="saveMemo" class="tw-w-full">
                    <v-container>
                        <v-row>
                            <v-textarea v-model="member.availability_memo"></v-textarea>
                        </v-row>
                        <v-row>
                            <div v-show="member.availability_memo?.length > 50" class="memo-warning">Memos should be 50 characters or less</div>
                        </v-row>
                    </v-container>
                </internal-table-section>

                <internal-table-section section-title="Historical Availabilities" :has-button="false" class="tw-w-full">
                    <smart-table
                        :items="availability_history"
                        :headers="headers_historical"
                        hide-default-header
                        hide-default-footer
                        disable-pagination
                        @click:row="editRecord"
                    >
                    </smart-table>
                </internal-table-section>
            </div>

            <div class="tw-flex tw-flex tw-w-2/3 tw-flex-wrap tw-justify-between tw-items-start tw-gap-4"><!-- right side -->
                <div v-for="availability in availability_upcoming" class="tw-rounded-lg tw-shadow tw-p-4" style="width: calc(50% - 0.5rem); border-top: 12px solid #67cea3;">
                    <v-card flat>
                        <v-card-title class="pt-0 px-0">{{ getAvailabilityName(availability) }}</v-card-title>
                        <v-card-subtitle class="px-0">{{ formatTimestamp(availability?.date_start) }} <span v-if="availability?.date_end && availability.date_end !== availability?.date_start"> - {{ formatTimestamp(availability.date_end) }}</span></v-card-subtitle>
                        <v-card-text class="px-0">
                            <v-tooltip top>
                                <template v-slot:activator="{on}">
                                    <v-chip v-on="on" v-show="availability.is_override">Override</v-chip>
                                </template>
                                <span>Override</span>
                            </v-tooltip>
                            <v-textarea
                                v-model="availability.notes"
                                auto-grow
                                label="Notes"
                                disabled
                            />
                        </v-card-text>
                        <v-card-actions class="pb-0 px-0">
                            <v-btn class="main-btn" @click="editRecord(availability)" :loading="is_loading">
                                Edit
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </div>
            </div>
        </div>
    </div>

</template>

<script>
    import InternalHeader from "@/Generic/InternalHeader";
    import InternalTableSection from "@/Generic/InternalTableSection";
    import EditMemberAvailabilityDialog from "@/Modals/EditMemberAvailabilityDialog.vue";
    import SmartTable from "@/Generic/SmartTable.vue";

    export default {
        name: "MembersShowAvailabilities",
        components: {
            SmartTable,
            EditMemberAvailabilityDialog,
            InternalHeader,
            InternalTableSection,
        },
        props: {
            'input': {
                type: Object,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'constants': {
                type: Object,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'valuelists': {
                type: Object,
                required: false,
                default: function () {
                    return undefined
                }
            }
        },
        data() {
            return {
                member: this.input,
                constant: this.constants,
                valuelist: this.valuelists,
                dialog_availability: false,
                selected_availability: {},
                is_loading: true,
                production_names: null,
            }
        },
        computed: {
            availability_upcoming: function () {
                return this.member.availability.filter(record => {
                    let today = new moment()
                    return record.date_end >= today.format("YYYY-MM-DD")
                })
            },
            availability_history: function () {
                let availability_history =  this.member.availability.filter(record => {
                    let today = new moment()
                    return record.date_end < today.format("YYYY-MM-DD")
                })
                availability_history = availability_history.sort((a,b) => {
                    if (a.date_end < b.date_end) {
                        return 1
                    }
                    if (a.date_end > b.date_end) {
                        return -1
                    }
                    return 0                })

                return availability_history;
            },
            headers_historical() {
                return [
                    {value: 'name',                 text: 'Name',   align: `start`, sortable: true,   filterable: false, },
                    {value: 'display_date_range',   text: 'Date',   align: `start`, sortable: true,   filterable: false, },
                ]
            },
        },
        mounted() {
            this.getDeferredValuelists()
        },
        methods: {
            getDeferredValuelists() {
                axios.post(route('productions.valuelist'))
                    .then((response)=>{
                        this.production_names = response?.data?.map(prod => prod.text)
                        this.is_loading = false
                    })
            },
            formatTimestamp(timestamp) {
                return new Date(timestamp).toISOString().split('T')[0]
            },
            editRecord(record) {
                this.selected_availability = record
                this.dialog_availability = true
            },
            addRecord() {
                this.selected_availability = null
                this.dialog_availability = true
            },
            saveMemo() {
                axios.put(
                    route('members.update', this.member.id).url(),
                    {id:this.member.id, availability_memo: this.member.availability_memo}
                ).then(response => {
                    if (!response.data.success) {
                        this.$root.snackbar.error(response.data?.message)
                        return false
                    }
                    this.$root.snackbar.success("Availability Memo Updated")
                }).catch(error => {
                    this.$root.snackbar.error(error)
                })
            },
            findValuelist(list, id) {
                if (list && id) {
                    return list.find(obj => obj.value === id)
                } else {
                    return ""
                }
            },
            getAvailabilityName(availability) {
                return availability?.production?.name || availability?.name
            }
        },
        watch: {
            input(newInput, oldInput) {
                this.member = newInput
            },
        }
    }
</script>

<style scoped>
.memo-warning {
    color: darkred;
    font-weight: bold;
}
</style>
